import React from 'react';
import BooleanRadio from '../../components/common/BooleanRadio/BooleanRadio';
import { AppFormSearchableSelect, AppFormTextarea, AppInputLabel } from '../../components/common/appForms';
import CopyTextIconButton from '../../components/common/CopyTextIconButton/CopyTextIconButton';
import { PUBLIC_WEEKLY_AVAILABILITY_OPTIONS } from '../Anamnesis/AnamnesisRegister/publicAnamnese.constants';
import styles from './Fup.module.css';
import {
  FUP_AVAILABILITY_HINT,
  FUP_AVAILABLE_TRAINING_TIME_OPTIONS,
  FUP_QUESTION_LABELS,
  FUP_TRAINING_LOCATION_OPTIONS,
  type FupQuestionnaireAnswers,
  type FupQuestionnaireErrors,
} from './fupQuestionnaire';

interface FupQuestionnaireFieldsProps {
  answers: FupQuestionnaireAnswers;
  errors: FupQuestionnaireErrors;
  disabled?: boolean;
  autoFocus?: boolean;
  showCopyButtons?: boolean;
  onChange: <K extends keyof FupQuestionnaireAnswers>(
    field: K,
    value: FupQuestionnaireAnswers[K],
  ) => void;
}

const FupQuestionnaireFields: React.FC<FupQuestionnaireFieldsProps> = ({
  answers,
  errors,
  disabled = false,
  autoFocus = false,
  showCopyButtons = false,
  onChange,
}) => {
  const copyButton = (value: string, label: string) => (
    showCopyButtons
      ? <CopyTextIconButton value={value} ariaLabel={`Copiar ${label.toLowerCase()}`} />
      : undefined
  );

  const timeOptions = answers.format === 'legacy'
    ? [
        ...FUP_AVAILABLE_TRAINING_TIME_OPTIONS,
        ...(!FUP_AVAILABLE_TRAINING_TIME_OPTIONS.some((option) => option.value === '120 min')
          ? [{ value: '120 min', label: '120 min' }]
          : []),
        ...(answers.availableTrainingTime
          && ![...FUP_AVAILABLE_TRAINING_TIME_OPTIONS, { value: '120 min', label: '120 min' }]
            .some((option) => option.value === answers.availableTrainingTime)
          ? [{ value: answers.availableTrainingTime, label: answers.availableTrainingTime }]
          : []),
      ]
    : FUP_AVAILABLE_TRAINING_TIME_OPTIONS;

  if (answers.format === 'legacy') {
    return (
      <>
        <div className={styles.feedbackField}>
          <AppFormTextarea
            id="fup-last-cycle"
            name="lastCycleFeedback"
            label={FUP_QUESTION_LABELS.lastCycleFeedback}
            labelAction={copyButton(answers.lastCycleFeedback, 'resposta de como foi o treino')}
            autoFocus={autoFocus}
            value={answers.lastCycleFeedback}
            onChange={(event) => onChange('lastCycleFeedback', event.target.value)}
            placeholder="O que funcionou bem e o que não funcionou"
            error={errors.lastCycleFeedback}
            disabled={disabled}
            required
          />
        </div>

        <div className={styles.feedbackField}>
          <AppFormTextarea
            id="fup-next-cycle-adjustments"
            name="nextCycleAdjustments"
            label={FUP_QUESTION_LABELS.nextCycleAdjustments}
            labelAction={copyButton(answers.nextCycleAdjustments, 'resposta sobre ajustes do próximo ciclo')}
            value={answers.nextCycleAdjustments}
            onChange={(event) => onChange('nextCycleAdjustments', event.target.value)}
            placeholder="Dor, desconforto, foco em algum grupamento, local de treino..."
            error={errors.nextCycleAdjustments}
            disabled={disabled}
            required
          />
        </div>

        <div className={styles.availabilityBlock}>
          <div className={styles.availabilityHeading}>
            <p className={styles.availabilityTitle}>{FUP_QUESTION_LABELS.legacyWeeklyAvailability}</p>
          </div>
          <p className={styles.availabilityHint}>{FUP_AVAILABILITY_HINT}</p>
          <div className={styles.availabilityGrid}>
            <AppFormSearchableSelect
              id="fup-weekly-availability"
              name="weeklyAvailability"
              label="Frequência semanal"
              labelAction={copyButton(answers.weeklyAvailability, 'disponibilidade semanal')}
              value={answers.weeklyAvailability}
              onChange={(event) => onChange('weeklyAvailability', event.target.value)}
              options={PUBLIC_WEEKLY_AVAILABILITY_OPTIONS}
              searchPlaceholder="Pesquisar"
              error={errors.weeklyAvailability}
              disabled={disabled}
              required
              allowEmpty={false}
            />
            <AppFormSearchableSelect
              id="fup-available-training-time"
              name="availableTrainingTime"
              label={FUP_QUESTION_LABELS.legacyAvailableTrainingTime}
              labelAction={copyButton(answers.availableTrainingTime, 'tempo disponível para treino')}
              value={answers.availableTrainingTime}
              onChange={(event) => onChange('availableTrainingTime', event.target.value)}
              options={timeOptions}
              searchPlaceholder="Pesquisar"
              error={errors.availableTrainingTime}
              disabled={disabled}
              required
              allowEmpty={false}
            />
          </div>
        </div>

        <div className={styles.feedbackField}>
          <AppFormTextarea
            id="fup-additional-notes"
            name="additionalNotes"
            label={FUP_QUESTION_LABELS.legacyAdditionalNotes}
            labelAction={copyButton(answers.additionalNotes, 'resposta sobre outros pontos')}
            value={answers.additionalNotes}
            onChange={(event) => onChange('additionalNotes', event.target.value)}
            placeholder="Se quiser, deixe mais algum ponto registrado"
            error={errors.additionalNotes}
            disabled={disabled}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.feedbackField}>
        <AppFormSearchableSelect
          id="fup-training-location"
          name="trainingLocation"
          label={FUP_QUESTION_LABELS.trainingLocation}
          labelAction={copyButton(answers.trainingLocation, 'local de treino')}
          value={answers.trainingLocation}
          onChange={(event) => onChange('trainingLocation', event.target.value)}
          options={[...FUP_TRAINING_LOCATION_OPTIONS]}
          searchPlaceholder="Pesquisar"
          error={errors.trainingLocation}
          disabled={disabled}
          required
          allowEmpty={false}
        />
      </div>

      <div className={styles.availabilityBlock}>
        <div className={styles.availabilityHeading}>
          <p className={styles.availabilityTitle}>{FUP_QUESTION_LABELS.weeklyAvailability}</p>
        </div>
        <p className={styles.availabilityHint}>{FUP_AVAILABILITY_HINT}</p>
        <div className={styles.availabilityGrid}>
          <AppFormSearchableSelect
            id="fup-weekly-availability"
            name="weeklyAvailability"
            label="Frequência semanal"
            labelAction={copyButton(answers.weeklyAvailability, 'disponibilidade semanal')}
            value={answers.weeklyAvailability}
            onChange={(event) => onChange('weeklyAvailability', event.target.value)}
            options={PUBLIC_WEEKLY_AVAILABILITY_OPTIONS}
            searchPlaceholder="Pesquisar"
            error={errors.weeklyAvailability}
            disabled={disabled}
            required
            allowEmpty={false}
          />
          <AppFormSearchableSelect
            id="fup-available-training-time"
            name="availableTrainingTime"
            label={FUP_QUESTION_LABELS.availableTrainingTime}
            labelAction={copyButton(answers.availableTrainingTime, 'tempo por treino')}
            value={answers.availableTrainingTime}
            onChange={(event) => onChange('availableTrainingTime', event.target.value)}
            options={timeOptions}
            searchPlaceholder="Pesquisar"
            error={errors.availableTrainingTime}
            disabled={disabled}
            required
            allowEmpty={false}
          />
        </div>
      </div>

      <div className={styles.feedbackField}>
        <AppInputLabel compact required>
          {FUP_QUESTION_LABELS.hasPainOrLimitation}
        </AppInputLabel>
        <BooleanRadio
          name="hasPainOrLimitation"
          value={answers.hasPainOrLimitation}
          onChange={(value) => {
            onChange('hasPainOrLimitation', value);
            if (!value) {
              onChange('painOrLimitationDetails', '');
            }
          }}
          disabled={disabled}
          error={errors.hasPainOrLimitation}
        />
      </div>

      {answers.hasPainOrLimitation === true && (
        <div className={styles.feedbackField}>
          <AppFormTextarea
            id="fup-pain-details"
            name="painOrLimitationDetails"
            label={FUP_QUESTION_LABELS.painOrLimitationDetails}
            labelAction={copyButton(answers.painOrLimitationDetails, 'detalhes da dor ou limitação')}
            value={answers.painOrLimitationDetails}
            onChange={(event) => onChange('painOrLimitationDetails', event.target.value)}
            placeholder="Descreva a dor/desconforto e os movimentos que parecem gerar"
            error={errors.painOrLimitationDetails}
            disabled={disabled}
            required
          />
        </div>
      )}

      <div className={styles.feedbackField}>
        <AppFormTextarea
          id="fup-additional-notes"
          name="additionalNotes"
          label={`${FUP_QUESTION_LABELS.additionalNotes} (opcional)`}
          labelAction={copyButton(answers.additionalNotes, 'mudança importante')}
          value={answers.additionalNotes}
          onChange={(event) => onChange('additionalNotes', event.target.value)}
          placeholder="Se quiser, conte alguma mudança importante"
          error={errors.additionalNotes}
          disabled={disabled}
        />
      </div>
    </>
  );
};

export default FupQuestionnaireFields;
